
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { EnhancedButton } from "@/components/ui/enhanced-button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { EnhancedCreateNewCard } from "@/components/documents/EnhancedCreateNewCard";
import { DocumentLibrary } from "@/components/documents/DocumentLibrary";
import { SmartHint } from "@/components/ui/smart-hint";
import { enhancedDocumentTypes } from "@/components/documents/enhanced-document-types";
import { Document } from "@/components/documents/types";
import { LoadingState } from "@/components/ui/loading-states";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { AssessmentGate } from "@/components/dashboard/AssessmentGate";
import { usePersona } from "@/hooks/use-persona";

export default function Documents() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { personaConfig } = usePersona();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .order("updated_at", { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error: any) {
      console.error("Error fetching documents:", error);
      toast({
        title: "Error",
        description: "Failed to load documents. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssessmentComplete = () => {
    // Refresh the page or update state as needed
    window.location.reload();
  };

  // Filter enhanced document types based on current persona
  const personaDocumentTypes = enhancedDocumentTypes.filter(type => 
    personaConfig.documentTypes.includes(type.id)
  );

  const handleNavigate = (id: string) => {
    navigate(`/documents/${id}`);
  };

  const handleDownload = async (doc: Document) => {
    try {
      setDownloadingId(doc.id);
      const { data, error } = await supabase.functions.invoke("generate-pdf", {
        body: { 
          documentId: doc.id,
          type: doc.type,
          content: doc.content
        }
      });

      if (error) throw error;
      if (!data?.url) throw new Error('No download URL generated');

      const link = document.createElement('a');
      link.href = data.url;
      link.download = `${doc.title.toLowerCase().replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Success",
        description: "Document downloaded successfully."
      });
    } catch (error: any) {
      console.error("Error downloading document:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to download document. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("documents")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setDocuments(documents.filter(doc => doc.id !== id));
      toast({
        title: "Success",
        description: "Document deleted successfully."
      });
    } catch (error: any) {
      console.error("Error deleting document:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete document. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading your documents..." />;
  }

  const DocumentsContent = () => (
    <div className="container mx-auto px-4 py-4 sm:py-8 max-w-7xl">
      <div className="mb-4 sm:mb-6">
        <EnhancedButton
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 hover:bg-gray-100 mb-2"
          size="sm"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Back to Dashboard</span>
          <span className="sm:hidden">Back</span>
        </EnhancedButton>
      </div>

      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          {personaConfig.name} Documents
        </h1>
        <p className="text-gray-600 text-sm sm:text-base">
          Create and manage documents optimized for {personaConfig.title.toLowerCase()}
        </p>
      </div>

      <Tabs defaultValue="create" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <TabsList className="grid w-full sm:w-auto grid-cols-2">
            <TabsTrigger value="create" className="text-xs sm:text-sm">
              <span className="hidden sm:inline">Create New</span>
              <span className="sm:hidden">Create</span>
            </TabsTrigger>
            <TabsTrigger value="library" className="text-xs sm:text-sm">
              <span className="hidden sm:inline">Document Library ({documents.length})</span>
              <span className="sm:hidden">Library ({documents.length})</span>
            </TabsTrigger>
          </TabsList>
          <div className="relative w-full sm:w-auto">
            <SmartHint
              id="documents-hint"
              title="Assessment-Enhanced Creation"
              description="Your documents are now pre-filled with your business assessment data for faster creation!"
              position="bottom"
              className="w-full sm:w-auto"
            />
          </div>
        </div>

        <TabsContent value="create">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {personaDocumentTypes.map((type) => (
              <EnhancedCreateNewCard key={type.id} type={type} />
            ))}
          </div>
          {personaDocumentTypes.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No document types available for this persona.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="library" className="space-y-6">
          <div className="min-h-screen">
            <DocumentLibrary
              documents={documents}
              onNavigate={handleNavigate}
              onDownload={handleDownload}
              onDelete={handleDelete}
              downloadingId={downloadingId}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  return (
    <ErrorBoundary>
      <AssessmentGate onAssessmentComplete={handleAssessmentComplete}>
        <DocumentsContent />
      </AssessmentGate>
    </ErrorBoundary>
  );
}
